import { useState, useEffect, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';

interface SafeAreaState {
  top: number;
  bottom: number;
  left: number;
  right: number;
  platform: 'android' | 'web';
}

// Valor padrão para a altura da status bar no Android
const ANDROID_STATUS_BAR_HEIGHT = 24;

/**
 * Hook para obter os valores de safe area (área segura) da tela.
 * Em dispositivos móveis, ele se adapta à status bar e à home bar.
 * No desktop, retorna zero.
 */
export function useSafeArea(): SafeAreaState {
  const [safeArea, setSafeArea] = useState<SafeAreaState>({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    platform: 'web'
  });

  const platform = useMemo(() => {
    return Capacitor.getPlatform() as 'android' | 'web';
  }, []);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      setSafeArea({ top: 0, bottom: 0, left: 0, right: 0, platform: 'web' });
      return;
    }

    const getSafeAreaInsets = async () => {
      let topOffset = 0;
      let bottomOffset = 0;

      if (platform === 'android') {
        try {
          // Tenta usar a variável CSS do Capacitor
          const topInset = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top').trim();
          topOffset = topInset ? parseFloat(topInset) : ANDROID_STATUS_BAR_HEIGHT;
        } catch {
          topOffset = ANDROID_STATUS_BAR_HEIGHT; // Fallback
        }
      }

      setSafeArea({
        top: topOffset,
        bottom: bottomOffset,
        left: 0,
        right: 0,
        platform: platform
      });
    };

    getSafeAreaInsets();

    const handleResize = () => getSafeAreaInsets();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [platform]);

  return safeArea;
}

// Função auxiliar para obter altura da StatusBar no Android
async function getAndroidStatusBarHeight(): Promise<number> {
  try {
    // Método 1: Tentar usar CSS env() primeiro
    const cssValue = getCSSEnvValue('safe-area-inset-top');
    if (cssValue > 0) {
      return cssValue;
    }

    // Método 2: Calcular baseado na densidade da tela
    const density = window.devicePixelRatio || 1;
    const baseHeight = 24; // Altura base da StatusBar em dp
    const calculatedHeight = Math.round(baseHeight * density);
    
    // Método 3: Usar valores conhecidos baseados na resolução
    const screenHeight = window.screen.height;
    if (screenHeight >= 2400) {
      return 48; // Dispositivos high-end com StatusBar maior
    } else if (screenHeight >= 1920) {
      return 36; // Dispositivos Full HD
    } else {
      return calculatedHeight; // Fallback calculado
    }
  } catch (error) {
    return 24; // Fallback final
  }
}

// Função auxiliar para obter valores CSS env()
function getCSSEnvValue(property: string): number {
  if (typeof window === 'undefined') return 0;

  try {
    // Criar elemento temporário para testar CSS env()
    const testElement = document.createElement('div');
    testElement.style.position = 'fixed';
    testElement.style.top = '0';
    testElement.style.left = '0';
    testElement.style.width = '1px';
    testElement.style.height = `env(${property}, 0px)`;
    testElement.style.visibility = 'hidden';
    testElement.style.pointerEvents = 'none';
    
    document.body.appendChild(testElement);
    const height = testElement.offsetHeight;
    document.body.removeChild(testElement);
    
    return height;
  } catch (error) {
    return 0;
  }
}

// Fallbacks para iOS baseados em modelos conhecidos
function getIOSFallback(): number {
  if (typeof window === 'undefined') return 44;

  const screenHeight = window.screen.height;
  const screenWidth = window.screen.width;

  // iPhone com notch/Dynamic Island (valores aproximados)
  if (screenHeight >= 812 || screenWidth >= 812) {
    // iPhone X, XS, 11, 12, 13, 14, 15 series
    if (screenHeight >= 926 || screenWidth >= 926) {
      return 59; // iPhone 14 Pro Max, 15 Pro Max
    } else if (screenHeight >= 896 || screenWidth >= 896) {
      return 48; // iPhone XR, 11, 12, 13, 14, 15
    } else {
      return 44; // iPhone X, XS, 11 Pro, 12 mini
    }
  }

  // iPhone sem notch
  return 20; // iPhone 8 e anteriores
}

// Hook simplificado - TopBar como parte da Safe Area
export function useLayoutSpacing(): {
  safeAreaOnly: number;
  safeAreaConfig: SafeAreaState;
  // Nova abordagem: TopBar dinâmico baseado na plataforma
  topBarHeight: number; // Altura total do TopBar (Safe Area + base)
  topBarVisibleHeight: number; // Altura visual do TopBar (apenas base)
  contentPadding: number; // Padding correto para o conteúdo
  isWeb: boolean; // Indica se está rodando no navegador
} {
  const safeAreaConfig = useSafeArea();
  const isWeb = safeAreaConfig.platform === 'web';
  
  // 🔧 ALTURA DINÂMICA: Menor para web, maior para mobile
  const baseTopBarHeight = isWeb ? 64 : 56; // h-16 para web, h-14 para mobile base
  const topBarHeight = safeAreaConfig.top + baseTopBarHeight; // Total (Safe Area + base)
  const topBarVisibleHeight = baseTopBarHeight; // Apenas a parte visível
  
  // 🔧 CORREÇÃO: Conteúdo deve começar após a altura visual do TopBar
  const contentPadding = topBarVisibleHeight;

  return {
    safeAreaOnly: safeAreaConfig.top,
    safeAreaConfig,
    topBarHeight, // Total (para referência)
    topBarVisibleHeight, // Visual (para CSS do TopBar)
    contentPadding, // Correto para o conteúdo
    isWeb,
  };
} 